import bind from 'bind-decorator';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import App from '../App';
import { Person, ViewHeightType } from '../tool/Person';

import { AppStateManager } from '@egr/xbox/base-app/managers/Manager';

import { Camera } from '@egr/wcf/modules/core/view';
import { CameraMode } from '@egr/wcf/modules/core/view/CameraControl';

import { Animation } from '@egr/room/modules/utils/Animation';

type ErrorViewType = 'unspecified' | 'catalog' | 'article' | 'configuration' | 'unlicensed';

type HeightActiveButton = 'child' | 'parent' | 'bird' | 'none';

export enum ViewLayer {
    None = -1,
    Article,
    Catalog,
    Loading,
    Error,
}

interface InitialCamera {
    position: Vector3;
    target: Vector3;
}

interface ViewManagerState {
    activeView: ViewLayer;
    errorView: ErrorViewType;
    allowViewChange: boolean;
    heightActiveButton: HeightActiveButton;
    currentViewingHeightValue: number;
    showGuide: boolean;
    showARSize: boolean;
    arStarted: boolean;
    initialCamera: InitialCamera | undefined;
    keyboardLayout: 'default' | 'fr';
}

export class ViewManager extends AppStateManager<ViewManagerState, App> {
    protected viewChangeTimeout: number | null = null;

    constructor() {
        super();
        this.setChildHeight = this.setChildHeight.bind(this);
        this.setCanvcasFocus = this.setCanvcasFocus.bind(this);
        this.setParentHeight = this.setParentHeight.bind(this);
        this.setBirdHeight = this.setBirdHeight.bind(this);
        this.setInitialCamera = this.setInitialCamera.bind(this);
        this.animateToInitialCamera = this.animateToInitialCamera.bind(this);
    }

    public set arStarted(arStarted: boolean) {
        this.updateState({ arStarted });
    }

    public showView(nextView: ViewLayer): boolean {
        if (this.app.sceneManager.viewer.view.cameraControl.cameraMode !== CameraMode.Perspective) {
            this.app.sceneManager.viewer.view.cameraControl.cameraMode = CameraMode.Perspective;
        }
        this.updateState({
            activeView: nextView,
            allowViewChange: false,
        });

        switch (nextView) {
            case ViewLayer.Article:
                if (!this.app.sceneManager.viewer.renderingEnabled) {
                    this.app.sceneManager.viewer.endDisableRendering();
                }
                this.app.sceneManager.viewer.resize();
                break;
            default:
                if (this.app.sceneManager.viewer.renderingEnabled) {
                    this.app.sceneManager.viewer.beginDisableRendering();
                }
        }

        this.viewChangeTimer();
        return true;
    }

    public showErrorView(errorView: ErrorViewType): boolean {
        this.updateState({
            errorView: errorView
        });

        return this.showView(ViewLayer.Error);
    }

    public setInitialCamera(): void {
        if (this.app.sceneManager.cameraPathInfo.available) {
            this.app.roomManager.showCamPathView(0);
        }

        const camera: Camera = this.app.coreApp.viewer.view.camera;

        const initialCamera: InitialCamera = {
            position: camera.position.clone(),
            target: camera.target.clone()
        };
        this.updateState({initialCamera});
    }

    public animateToInitialCamera(): void {
        if (this.app.roomManager.cameraPathExists()) {
            return this.app.roomManager.showCamPathView(0);
        }

        if (this.state.initialCamera == null) {
            return;
        }

        const animationOptions: Animation.AnimationOptions = {
            StartPos: this.app.coreApp.viewer.view.camera.position.clone(),
            StartTarget: this.app.coreApp.viewer.view.camera.target.clone(),
            EndPos: this.state.initialCamera.position,
            EndTarget: this.state.initialCamera.target
        };
        Animation.BackToStart(this.app.coreApp, animationOptions, 5);

        this.updateState({heightActiveButton: 'parent', currentViewingHeightValue: 50});
    }

    public setActivePersonHeightButton(): void {
        const height: number = this.app.coreApp.viewer.view.camera.position.y;
        switch (height) {
            case ViewHeightType.CHILD:
                this.updateState({heightActiveButton: 'child', currentViewingHeightValue: 0});
                break;
            case ViewHeightType.PARENT:
                this.updateState({heightActiveButton: 'parent', currentViewingHeightValue: 50});
                break;
            case ViewHeightType.BIRD:
                this.updateState({heightActiveButton: 'bird', currentViewingHeightValue: 100});
                break;
            default:
                this.updateState({heightActiveButton: 'none'});
                break;
        }
    }

    public setCanvcasFocus(): void {
        this.app.sceneManager.viewer.canvas.focus();
    }

    public setChildHeight(): void {
        Person.setHeight(this.app.coreApp.viewer.view, ViewHeightType.CHILD);
        this.setActivePersonHeightButton();
    }

    public setParentHeight(): void {
        Person.setHeight(this.app.coreApp.viewer.view, ViewHeightType.PARENT);
        this.setActivePersonHeightButton();
    }

    public setBirdHeight(): void {
        Person.setHeight(this.app.coreApp.viewer.view, ViewHeightType.BIRD);
        this.setActivePersonHeightButton();
    }

    public setCustomViewerHeight(height: number, scaledHeight: number): void {
        this.updateState({currentViewingHeightValue: height});
        Person.setHeight(this.app.coreApp.viewer.view, scaledHeight);
        this.setActivePersonHeightButton();
    }

    public showGuide(showGuide: boolean): void {
        this.updateState({ showGuide });
    }

    public showARSize(showARSize: boolean): void {
        this.updateState({ showARSize });
    }

    @bind
    public async updateKeyboardLayout(): Promise<void> {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const keyboard: any = (navigator as any).keyboard;
            const keyboardLayoutMap: Map<string, string> = await keyboard.getLayoutMap();
            if (keyboardLayoutMap.get('KeyA') === 'q') {
                return this.updateState({keyboardLayout: 'fr'});
            }
            return this.updateState({keyboardLayout: 'default'});
        } catch (e) {
            /*
                https://developer.mozilla.org/en-US/docs/Web/API/Keyboard/getLayoutMap
                only available in Chrome 69+, Edge 79+ and Opera 56+
            */
        }

        if (['fr', 'fr-fr'].indexOf(navigator.language.toLocaleLowerCase()) !== -1) {
            return this.updateState({keyboardLayout: 'fr'});
        }

        return this.updateState({keyboardLayout: 'default'});
    }

    /**
     * Set a public boolean attribut to true for 1 second when view changes,
     * so this can be checked before trying to change the view again in a short time window.
     * This should prevents accidantal closing of project when user double taps on article-info view button
     */
    protected viewChangeTimer = (): void => {
        if (this.viewChangeTimeout != null) {
            clearTimeout(this.viewChangeTimeout);
        }
        this.viewChangeTimeout = window.setTimeout(
            () => {
                this.updateState({allowViewChange: true});
            },
            1000
        );
    };

    protected getInitialState(): ViewManagerState {
        return {
            activeView: ViewLayer.Loading,
            errorView: 'unspecified',
            allowViewChange: true,
            heightActiveButton: 'parent',
            currentViewingHeightValue: 50,
            showGuide: true,
            showARSize: false,
            arStarted: false,
            initialCamera: undefined,
            keyboardLayout: 'default'
        };
    }
}
